import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export interface SelectorOption<T extends string> {
  value: T;
  label: string;
  selectedLabel?: string;
  searchText?: string;
  secondary?: ReactNode;
}

export function filterSelectorOptions<T extends string>(
  options: readonly SelectorOption<T>[],
  query: string,
): readonly SelectorOption<T>[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === "") return options;
  return options.filter((option) =>
    `${option.label} ${option.searchText ?? ""}`
      .toLocaleLowerCase()
      .includes(needle),
  );
}

export function Selector<T extends string>({
  options,
  value,
  onValueChange,
  searchable = false,
  label,
  ariaLabel,
  placeholder = "Select…",
  disabled = false,
  autoFocus = false,
  className,
  searchValue,
  onSearchChange,
  onSearchInputChange,
  portalMenu = false,
  triggerContent,
}: {
  options: readonly SelectorOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  searchable?: boolean;
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  searchValue?: string;
  onSearchChange?: (query: string) => void;
  onSearchInputChange?: (query: string) => void;
  portalMenu?: boolean;
  triggerContent?: ReactNode;
}) {
  const inputId = useId();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [internalQuery, setInternalQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [portalStyle, setPortalStyle] = useState<CSSProperties | null>(null);
  const query = searchValue ?? internalQuery;
  const selected = options.find((option) => option.value === value);
  const visible = useMemo(
    () =>
      filterSelectorOptions(options, query).slice(
        0,
        searchable ? 50 : options.length,
      ),
    [options, query, searchable],
  );
  const choose = (next: T) => {
    onValueChange(next);
    setInternalQuery("");
    onSearchChange?.("");
    setOpen(false);
  };
  const updateQuery = (next: string) => {
    setInternalQuery(next);
    onSearchChange?.(next);
  };

  useLayoutEffect(() => {
    if (!open || !portalMenu) {
      setPortalStyle(null);
      return;
    }
    const placeMenu = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.max(220, rect.width);
      const estimatedHeight = Math.min(280, visible.length * 50);
      const gap = 5;
      const roomBelow = window.innerHeight - rect.bottom - gap;
      const top =
        roomBelow >= estimatedHeight || roomBelow >= rect.top
          ? rect.bottom + gap
          : Math.max(gap, rect.top - estimatedHeight - gap);
      setPortalStyle({
        position: "fixed",
        top,
        left: Math.max(
          gap,
          Math.min(rect.left, window.innerWidth - width - gap),
        ),
        width,
      });
    };
    placeMenu();
    window.addEventListener("resize", placeMenu);
    window.addEventListener("scroll", placeMenu, true);
    return () => {
      window.removeEventListener("resize", placeMenu);
      window.removeEventListener("scroll", placeMenu, true);
    };
  }, [open, portalMenu, visible.length]);

  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (
        rootRef.current?.contains(event.target) ||
        menuRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    return () =>
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
  }, [open]);

  const menu = open && visible.length > 0 && (
    <div
      ref={menuRef}
      id={listId}
      role="listbox"
      className={`ui-selector__menu${portalMenu ? " ui-selector__menu--portal" : ""}`}
      style={portalMenu ? (portalStyle ?? { visibility: "hidden" }) : undefined}
    >
      {visible.map((option) => (
        <button
          key={option.value}
          type="button"
          role="option"
          aria-selected={option.value === value}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => choose(option.value)}
        >
          <span className="ui-selector__option-copy">
            <strong>{option.label}</strong>
            {option.secondary && <small>{option.secondary}</small>}
          </span>
          {option.value === value && <span aria-hidden="true">✓</span>}
        </button>
      ))}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={[
        "ui-selector",
        searchable && "ui-selector--searchable",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      {label && <label htmlFor={inputId}>{label}</label>}
      {searchable ? (
        <input
          id={inputId}
          role="combobox"
          type="search"
          value={
            open && query !== ""
              ? query
              : (selected?.selectedLabel ?? selected?.label ?? query)
          }
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel ?? label ?? "Selector"}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={open}
          onFocus={(event) => {
            event.currentTarget.select();
            setOpen(true);
          }}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            onSearchInputChange?.(event.target.value);
            updateQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" && visible[0]) {
              event.preventDefault();
              choose(visible[0].value);
            }
            if (event.key === "Escape" && open) {
              event.stopPropagation();
              setOpen(false);
            }
          }}
        />
      ) : (
        <button
          id={inputId}
          type="button"
          role="combobox"
          disabled={disabled}
          autoFocus={autoFocus}
          aria-label={ariaLabel ?? label ?? "Selector"}
          aria-controls={listId}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === "Escape" && open) {
              event.stopPropagation();
              setOpen(false);
            }
          }}
        >
          <span className="ui-selector__trigger-content">
            {triggerContent ??
              selected?.selectedLabel ??
              selected?.label ??
              placeholder}
          </span>
          <svg
            className="ui-selector__chevron"
            viewBox="0 0 12 12"
            width="12"
            height="12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            aria-hidden="true"
          >
            <path d="m3 4.75 3 3 3-3" />
          </svg>
        </button>
      )}
      {portalMenu && menu ? createPortal(menu, document.body) : menu}
    </div>
  );
}
